import queryClient, { PERSIST_KEY } from '@utils/queryClient';
import { authClient } from './authClient';
import axios from 'axios';

const VITE_API_URL = import.meta.env.VITE_API_URL

const apiClient = axios.create({
  baseURL: `${VITE_API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
});

// BetterAuth manages session lifetime server-side (sliding `updateAge`), so there is no
// client-side token refresh. On a hard 401 we clear cached data and send the user to login.
const AUTH_PATHS = ['/login', '/signup', '/forgot-password'];

// Prevent concurrent 401s (multiple in-flight requests) from each triggering a redirect —
// only the first one clears the cache and navigates.
let redirectingToLogin = false;

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const onAuthPage = AUTH_PATHS.some((p) => window.location.pathname.startsWith(p));
      if (!onAuthPage && !redirectingToLogin) {
        // A 401 is transient right after a first social sign-up, while the domain user is still
        // being provisioned server-side. Only eject to /login when there is genuinely no
        // session; otherwise let the request retry so it refetches once provisioning completes.
        const hasSession = await authClient
          .getSession()
          .then(({ data }) => !!data?.user)
          .catch(() => false);
        if (!hasSession && !redirectingToLogin) {
          redirectingToLogin = true;
          queryClient?.clear();
          // Remove the persisted cache too: queryClient.clear() only empties memory and the
          // persister's write is throttled, so a hard reload could otherwise rehydrate stale
          // auth state and re-enter the redirect loop.
          window.localStorage.removeItem(PERSIST_KEY);
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
